import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Star, Loader2, GitBranch, Download, ArrowUp, ArrowDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const emptyStep = { order: 1, nom: '', description: '', required_role: 'validateur', assigned_user_id: null, is_mandatory: true, can_skip_if_amount_below: null, timeout_days: null };

export default function Workflows() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  // Auto-open template picker if navigated from Settings ("Partir d'un modele")
  useEffect(() => {
    if (searchParams.get('openTemplates') === '1') {
      setTemplateDialogOpen(true);
      // Clean the URL so a refresh doesn't re-open it
      const next = new URLSearchParams(searchParams);
      next.delete('openTemplates');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nom: '', description: '', is_default: false, steps: [{ ...emptyStep }] });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [wRes, tRes] = await Promise.all([
        axios.get(`${API_URL}/workflows`),
        axios.get(`${API_URL}/workflows/templates`).catch(() => ({ data: [] }))
      ]);
      setWorkflows(wRes.data);
      setTemplates(tRes.data);
    } catch (e) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    if (!form.nom || form.steps.length === 0) { toast.error('Nom et au moins une étape requis'); return; }
    setSaving(true);
    try {
      const payload = { ...form, steps: form.steps.map((s, i) => ({ ...s, order: i + 1 })) };
      if (editing) { await axios.put(`${API_URL}/workflows/${editing}`, payload); toast.success('Workflow modifié'); }
      else { await axios.post(`${API_URL}/workflows`, payload); toast.success('Workflow créé'); }
      setDialogOpen(false); setEditing(null); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce workflow ?')) return;
    try { await axios.delete(`${API_URL}/workflows/${id}`); toast.success('Workflow supprimé'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleSetDefault = async (id) => {
    try { await axios.put(`${API_URL}/workflows/${id}/set-default`); toast.success('Workflow par defaut'); fetchData(); }
    catch (e) { toast.error('Erreur'); }
  };

  const handleImportTemplate = async (code) => {
    try { await axios.post(`${API_URL}/workflows/from-template/${code}`); toast.success('Workflow importe'); setTemplateDialogOpen(false); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openEdit = (w) => { setEditing(w.id); setForm({ nom: w.nom, description: w.description || '', is_default: w.is_default, steps: w.steps || [] }); setDialogOpen(true); };
  const addStep = () => setForm({ ...form, steps: [...form.steps, { ...emptyStep, order: form.steps.length + 1 }] });
  const removeStep = (i) => setForm({ ...form, steps: form.steps.filter((_, idx) => idx !== i) });
  const updateStep = (i, field, value) => { const s = [...form.steps]; s[i] = { ...s[i], [field]: value }; setForm({ ...form, steps: s }); };
  const moveStep = (i, dir) => { const s = [...form.steps]; const j = i + dir; if (j < 0 || j >= s.length) return; [s[i], s[j]] = [s[j], s[i]]; setForm({ ...form, steps: s }); };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold">Workflows de validation</h1><p className="text-slate-500">{workflows.length} workflow(s)</p></div>
        <div className="flex gap-2">
          <Button onClick={() => setTemplateDialogOpen(true)} className="bg-emerald-600 hover:bg-emerald-700"><Download className="w-4 h-4 mr-2" /> Partir d'un modèle</Button>
          <Button onClick={() => { setEditing(null); setForm({ nom: '', description: '', is_default: false, steps: [{ ...emptyStep }] }); setDialogOpen(true); }} variant="outline"><Plus className="w-4 h-4 mr-2" /> Nouveau workflow</Button>
        </div>
      </div>

      <div className="grid gap-4">
        {workflows.map(w => (
          <Card key={w.id}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <GitBranch className="w-5 h-5 text-[#0F5156]" />
                  <div><h3 className="font-semibold">{w.nom}</h3>{w.description && <p className="text-sm text-slate-500">{w.description}</p>}</div>
                  {w.is_default && <Badge className="bg-[#0F5156]">Par defaut</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{(w.steps || []).length} étape(s)</Badge>
                  {!w.is_default && <Button variant="ghost" size="sm" onClick={() => handleSetDefault(w.id)} title="Definir par defaut"><Star className="w-4 h-4" /></Button>}
                  <Button variant="ghost" size="sm" onClick={() => openEdit(w)}><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(w.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {(w.steps || []).map((s, i) => (
                  <React.Fragment key={i}>
                    <div className="px-3 py-1 bg-slate-100 dark:bg-slate-800 rounded-full text-sm">{s.nom} <span className="text-slate-400">({s.required_role})</span></div>
                    {i < (w.steps || []).length - 1 && <span className="text-slate-300">&rarr;</span>}
                  </React.Fragment>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
        {workflows.length === 0 && (
          <Card className="border-dashed border-2">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <GitBranch className="w-10 h-10 text-slate-300 mb-3" />
              <p className="text-slate-500 mb-4">Aucun workflow personnalisé. Démarrez avec un modèle pré-configuré.</p>
              <Button onClick={() => setTemplateDialogOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Download className="w-4 h-4 mr-2" /> Partir d'un modèle de workflow
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Modifier le workflow' : 'Nouveau workflow'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nom *</Label><Input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} /></div>
              <div className="flex items-center gap-2 pt-6"><Switch checked={form.is_default} onCheckedChange={v => setForm({ ...form, is_default: v })} /><Label>Workflow par defaut</Label></div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div><div className="flex items-center justify-between mb-2"><Label className="text-base font-semibold">Étapes de validation</Label><Button variant="outline" size="sm" onClick={addStep}><Plus className="w-3 h-3 mr-1" /> Étape</Button></div>
              {form.steps.map((s, i) => (
                <div key={i} className="flex items-center gap-2 p-3 border rounded-lg mb-2 bg-slate-50 dark:bg-slate-800">
                  <div className="flex flex-col gap-1"><Button variant="ghost" size="sm" onClick={() => moveStep(i, -1)} disabled={i === 0}><ArrowUp className="w-3 h-3" /></Button><Button variant="ghost" size="sm" onClick={() => moveStep(i, 1)} disabled={i === form.steps.length - 1}><ArrowDown className="w-3 h-3" /></Button></div>
                  <span className="text-sm font-mono text-slate-400 w-6">{i + 1}</span>
                  <Input placeholder="Nom de l'étape" value={s.nom} onChange={e => updateStep(i, 'nom', e.target.value)} className="flex-1" />
                  <Select value={s.required_role} onValueChange={v => updateStep(i, 'required_role', v)}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="validateur">Validateur</SelectItem><SelectItem value="comptable">Comptable</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                  </Select>
                  <div className="flex items-center gap-1"><Switch checked={s.is_mandatory} onCheckedChange={v => updateStep(i, 'is_mandatory', v)} /><span className="text-xs">Oblig.</span></div>
                  {form.steps.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeStep(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button><Button onClick={handleSave} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent><DialogHeader><DialogTitle>Importer un modele</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {templates.map(t => (
              <Card key={t.id} className="cursor-pointer hover:shadow-md" onClick={() => handleImportTemplate(t.code)}>
                <CardContent className="pt-4"><h4 className="font-semibold">{t.nom}</h4><p className="text-sm text-slate-500">{t.description}</p>
                  <div className="flex gap-1 mt-2">{(t.steps || []).map((s, i) => <Badge key={i} variant="outline" className="text-xs">{s.nom}</Badge>)}</div>
                </CardContent>
              </Card>
            ))}
            {templates.length === 0 && <p className="text-center text-slate-500">Aucun modele disponible</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
